import React, { useState, useEffect } from 'react';
import { useNav } from '../../hooks/nav';
import { ReactComponent as BackIcon } from '../../assets/images/arrowLeft.svg';
import Analytics from '@web-solutions/module-analytics';
import { t } from '@web-solutions/module-localization';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import Button from 'src/onboarding/components/ui/button/Button';
import yourBody from '../../assets/images/understandYourBody.png';
import styles from './style.module.scss';
import { Caption } from 'src/onboarding/components/typography/Caption/Caption';
import { ReactComponent as BodyTempIcon } from '../../assets/images/tempIcon.svg';
import { ReactComponent as EnergyLevelIcon } from '../../assets/images/energyIcon.svg';
import { ReactComponent as MenuIcon } from '../../assets/images/menuWhiteIcon.svg';
import { ReactComponent as MetabolismIcon } from '../../assets/images/bodyTypeIcon.svg';
import { ReactComponent as EfficiencyIcon } from '../../assets/images/efficiencyIcon.svg';
import { useStore } from 'src/onboarding/store/store';
import { renderHighlightedTitle } from 'src/onboarding/helpers/renderHighlightedTitle';

const InfoItem = ({ icon: Icon, titleKey, descriptionKey }) => (
  <div className={styles.infoItem}>
    <Icon />
    <div className={styles.infoDesc}>
      <Caption size='l'>{t(titleKey)}</Caption>
      <Caption size='m' className={styles.desc}>
        {t(descriptionKey)}
      </Caption>
    </div>
  </div>
);

const InfoSection = ({ titleKey, items }) => (
  <div className={styles.infoSection}>
    <Text size='m' bold>
      {t(titleKey)}
    </Text>
    {items.map((item, index) => (
      <InfoItem key={index} {...item} />
    ))}
  </div>
);

const AnimatedTitle = ({ title, duration = 2000 }) => {
  const [animatedTitle, setAnimatedTitle] = useState('');

  useEffect(() => {
    const percentageMatch = title.match(/(\d+)%/);

    if (!percentageMatch) {
      setAnimatedTitle(title);
      return;
    }

    const targetPercentage = parseInt(percentageMatch[1]);
    const parts = title.split(/(\d+%)/);

    let currentPercentage = 0;
    const increment = targetPercentage / (duration / 16);

    const timer = setInterval(() => {
      currentPercentage += increment;
      if (currentPercentage >= targetPercentage) {
        const finalTitle = title.replace(percentageMatch[0], `${targetPercentage}%`);
        setAnimatedTitle(finalTitle);
        clearInterval(timer);
      } else {
        const currentTitle = title.replace(percentageMatch[0], `${Math.floor(currentPercentage)}%`);
        setAnimatedTitle(currentTitle);
      }
    }, 16);

    return () => clearInterval(timer);
  }, [title, duration]);

  return (
    <Title
      tag='h2'
      style={{
        padding: '0 25px',
        textAlign: 'center',
        fontVariantNumeric: 'tabular-nums',
        width: 330,
      }}
    >
      {renderHighlightedTitle(animatedTitle || title)}
    </Title>
  );
};

function Page() {
  const pageAnswers = useStore((state) => state.pageAnswers);
  const { currentIndex, onPrev, onNext } = useNav();
  const isFirst = currentIndex === 0;

  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const SECTION_CONFIG = {
    yourData: {
      titleKey: 'onboarding.understandYourBody.yourData',
      items: [
        {
          icon: BodyTempIcon,
          titleKey: 'onboarding.understandYourBody.bodyTemp',
          descriptionKey: capitalize(pageAnswers['body-temperature']),
        },
        {
          icon: EnergyLevelIcon,
          titleKey: 'onboarding.understandYourBody.energyLevel',
          descriptionKey: capitalize(pageAnswers['energy-rhythm']),
        },
        {
          icon: MenuIcon,
          titleKey: 'onboarding.understandYourBody.hunger',
          descriptionKey: capitalize(pageAnswers['hunger-frequency']),
        },
      ],
    },
    insights: {
      titleKey: 'onboarding.understandYourBody.insights',
      items: [
        {
          icon: MetabolismIcon,
          titleKey: 'onboarding.understandYourBody.metabolismType',
          descriptionKey: 'onboarding.understandYourBody.detecting',
        },
        {
          icon: EnergyLevelIcon,
          titleKey: 'onboarding.understandYourBody.energyLevel',
          descriptionKey: 'onboarding.understandYourBody.evaluating',
        },
        {
          icon: EfficiencyIcon,
          titleKey: 'onboarding.understandYourBody.efficiency',
          descriptionKey: 'onboarding.understandYourBody.calculating',
        },
      ],
    },
  };

  const handleContinue = () => {
    Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
      page: 'understand-your-body',
    });
    onNext();
  };

  return (
    <div className={`page ${styles.top}`}>
      {!isFirst && (
        <div className={'back'} onClick={onPrev}>
          <BackIcon />
        </div>
      )}

      <div className={'wrapper-info'}>
        <AnimatedTitle title={t('onboarding.understandYourBody.title')} duration={2000} />
      </div>

      <div className={styles.image} style={{ backgroundImage: `url(${yourBody})` }} />

      <div className={styles.info}>
        <InfoSection {...SECTION_CONFIG.yourData} />
        <InfoSection {...SECTION_CONFIG.insights} />
        <div className={styles.blur}></div>
      </div>

      <div className={'next-wrapper'}>
        <Button onClick={handleContinue} title={t('onboarding.understandYourBody.continue')} />
      </div>
    </div>
  );
}

export default Page;
